import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Download, AlertTriangle, ShieldCheck, Tv } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import { usePermission } from '../../../context/PermissionContext';
import * as svc from '../../../services/safetyCalendarService';
import {
  SEVERITY, SEVERITY_ORDER, OSHA_CATEGORY, MONTHS,
  formatDateKey, parseDateKey, monthKey, formatTime,
} from './constants';
import CalendarGrid from './components/CalendarGrid';
import DayModal from './components/DayModal';
import BaselineModal from './components/BaselineModal';
import ExportModal from './components/ExportModal';

export default function SafetyCalendar() {
  const { currentUser } = useAuth();
  const { hasPermission } = usePermission();
  const canLog = hasPermission('safety_calendar_log') || hasPermission('safety_calendar_manage');
  const canManage = hasPermission('safety_calendar_manage');

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [latestOsha, setLatestOsha] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [showBaseline, setShowBaseline] = useState(false);
  const [showExport, setShowExport] = useState(false);

  const todayKey = formatDateKey(new Date());
  const ym = monthKey(currentMonth);

  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, oshaRes] = await Promise.all([svc.getEntries(ym), svc.getLatestOsha()]);
      setEntries(entriesRes.entries || []);
      setLatestOsha(oshaRes.date || null);
    } catch (e) {
      toast.error(e?.message || 'Failed to load safety calendar.');
    } finally {
      setLoading(false);
    }
  }, [ym]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  useEffect(() => {
    svc.getSettings()
      .then((r) => setBaseline(r.oshaBaselineDate || null))
      .catch(() => { /* non-fatal */ });
  }, []);

  const entriesByDay = useMemo(() => {
    const map = {};
    for (const e of entries) (map[e.incidentDate] = map[e.incidentDate] || []).push(e);
    return map;
  }, [entries]);

  const selectedDayEntries = selectedDateKey ? (entriesByDay[selectedDateKey] || []) : [];

  // Days since last OSHA reportable injury: later of latest logged entry or baseline.
  const daysSince = useMemo(() => {
    let last = latestOsha;
    let fromBaseline = false;
    if (baseline && (!last || baseline > last)) { last = baseline; fromBaseline = true; }
    if (!last) return { count: null, lastDate: null, fromBaseline: false };
    const lastMid = parseDateKey(last);
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const count = Math.max(0, Math.round((todayMid - lastMid) / 86400000));
    return { count, lastDate: last, fromBaseline };
  }, [latestOsha, baseline]);

  const monthStats = useMemo(() => {
    const stats = { green: 0, yellow: 0, orange: 0, red: 0 };
    const daysWithIncidents = new Set();
    for (const e of entries) {
      if (e.severity !== 'green') {
        stats[e.severity] = (stats[e.severity] || 0) + 1;
        daysWithIncidents.add(e.incidentDate);
      }
    }
    // Elapsed days this month up to today.
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    let elapsed = 0;
    for (let d = 1; d <= monthEnd.getDate(); d++) {
      const k = `${ym}-${String(d).padStart(2, '0')}`;
      if (k <= todayKey) elapsed++;
    }
    stats.green = Math.max(0, elapsed - daysWithIncidents.size);
    return stats;
  }, [entries, currentMonth, ym, todayKey]);

  const incidentDays = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (e.severity !== 'green') (map[e.incidentDate] = map[e.incidentDate] || []).push(e);
    }
    return Object.entries(map)
      .map(([dateKey, list]) => ({ dateKey, entries: list }))
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  }, [entries]);

  const goPrev = () => { const d = new Date(currentMonth); d.setMonth(d.getMonth() - 1); setCurrentMonth(d); };
  const goNext = () => { const d = new Date(currentMonth); d.setMonth(d.getMonth() + 1); setCurrentMonth(d); };
  const goToday = () => setCurrentMonth(new Date());

  // --- entry handlers (service throws -> caller in modal surfaces the message) ---
  const handleCreate = async (payload) => { await svc.createEntry(payload); toast.success('Entry saved.'); await loadMonth(); };
  const handleUpdate = async (id, payload) => { await svc.updateEntry(id, payload); toast.success('Entry updated.'); await loadMonth(); };
  const handleDelete = async (id) => { await svc.deleteEntry(id); toast.success('Entry deleted.'); await loadMonth(); };

  const handleSaveBaseline = async (date) => { const r = await svc.setOshaBaseline(date); setBaseline(r.oshaBaselineDate); setShowBaseline(false); toast.success('Baseline saved.'); };
  const handleClearBaseline = async () => { await svc.clearOshaBaseline(); setBaseline(null); setShowBaseline(false); toast.success('Baseline cleared.'); };

  const handleExport = async (scope, month) => { await svc.exportCsv(scope, month); toast.success('Export downloaded.'); };

  // Default export month = prior month.
  const priorMonthKey = useMemo(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return monthKey(d);
  }, []);

  const counterColor =
    daysSince.count === null ? 'text-slate-500' :
    daysSince.count === 0 ? 'text-red-500' :
    daysSince.count < 30 ? 'text-amber-400' : 'text-emerald-400';

  return (
    <div className="bg-slate-900 text-slate-200 rounded-lg border border-slate-700 min-h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="border-b border-slate-700 px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-blue-500" />
          <div>
            <div className="text-[10px] tracking-widest text-slate-500">SHARED SAFETY LOG</div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Safety Calendar</h1>
          </div>
        </div>
        <button
          onClick={() => setShowExport(true)}
          className="text-sm px-3 py-2 rounded-md border border-slate-600 text-slate-200 hover:bg-slate-700 flex items-center gap-2 self-start md:self-auto"
        >
          <Download className="w-4 h-4" /> Export
        </button>
      </div>

      {/* OSHA counter */}
      <div className="bg-slate-950/60 border-b border-slate-700 px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-5">
          <div className={`text-6xl md:text-7xl font-bold tabular-nums leading-none ${counterColor}`}>
            {daysSince.count !== null ? daysSince.count : '—'}
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-slate-500">DAYS SINCE LAST</div>
            <div className="text-lg font-bold text-white mt-0.5">OSHA Reportable Injury</div>
            <div className="text-[10px] tracking-wider text-slate-500 mt-1">
              {daysSince.lastDate
                ? `LAST INCIDENT: ${daysSince.lastDate}${daysSince.fromBaseline ? ' (BASELINE)' : ''}`
                : 'NO REPORTABLE INJURIES ON RECORD'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start md:self-auto">
          {/* Login-free signage page for the warehouse OptiSign displays.
              Plain <a>, not a Route — it is a static file, not part of the SPA. */}
          <a
            href="/safety-display.html"
            target="_blank"
            rel="noopener noreferrer"
            title="Open the public display page used by the warehouse screens"
            className="text-sm px-3 py-2 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700 flex items-center gap-2"
          >
            <Tv className="w-4 h-4" /> TV Display
          </a>
          {canManage && (
            <button
              onClick={() => setShowBaseline(true)}
              className="text-sm px-3 py-2 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              {baseline ? 'Edit Baseline' : 'Set Baseline'}
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Month nav + summary */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] tracking-widest text-slate-500 mb-2">PERIOD</div>
            <div className="flex items-center gap-2">
              <button onClick={goPrev} className="p-2 rounded-md border border-slate-600 hover:bg-slate-700 text-slate-300"><ChevronLeft className="w-4 h-4" /></button>
              <h2 className="text-xl font-bold text-white tabular-nums px-2 min-w-[200px]">{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h2>
              <button onClick={goNext} className="p-2 rounded-md border border-slate-600 hover:bg-slate-700 text-slate-300"><ChevronRight className="w-4 h-4" /></button>
              <button onClick={goToday} className="text-sm px-3 py-2 rounded-md border border-slate-600 hover:bg-slate-700 text-slate-300 ml-2">Today</button>
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-slate-500 mb-2">MONTH SUMMARY</div>
            <div className="flex flex-wrap items-center gap-2">
              {SEVERITY_ORDER.map((key) => {
                const sev = SEVERITY[key];
                return (
                  <div key={key} className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-sm ${sev.dot}`} />
                    <span className="text-[11px] tracking-wider text-slate-400">{sev.code}</span>
                    <span className="text-base font-bold text-white tabular-nums">{monthStats[key] || 0}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <CalendarGrid
          currentMonth={currentMonth}
          entriesByDay={entriesByDay}
          todayKey={todayKey}
          onSelectDay={setSelectedDateKey}
        />

        {!canLog && (
          <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> You have view-only access. Contact a Safety Lead to log entries.
          </div>
        )}

        {/* Incident log */}
        {incidentDays.length > 0 && (
          <div className="mt-6">
            <div className="flex items-baseline justify-between gap-2 mb-3">
              <div>
                <div className="text-[10px] tracking-widest text-slate-500">INCIDENT LOG</div>
                <div className="text-lg font-bold text-white mt-0.5">{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</div>
              </div>
              <div className="text-[10px] tracking-widest text-slate-500">
                {incidentDays.length} {incidentDays.length === 1 ? 'DAY' : 'DAYS'} WITH ISSUES
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 overflow-hidden divide-y divide-slate-700">
              {incidentDays.map((day) => {
                const date = parseDateKey(day.dateKey);
                const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return (
                  <div key={day.dateKey} className="flex flex-col md:flex-row">
                    <button
                      onClick={() => setSelectedDateKey(day.dateKey)}
                      className="md:w-44 flex-shrink-0 p-4 text-left bg-slate-800 hover:bg-slate-700 transition-colors border-b md:border-b-0 md:border-r border-slate-700"
                    >
                      <div className="text-[10px] tracking-widest text-slate-500">{day.dateKey}</div>
                      <div className="text-xl font-bold text-white mt-0.5 leading-none">{monthDay}</div>
                      <div className="text-[10px] tracking-wider text-slate-400 mt-1">{dayName.toUpperCase()}</div>
                      <div className="text-[10px] tracking-wider text-slate-500 mt-2">
                        {day.entries.length} {day.entries.length === 1 ? 'ENTRY' : 'ENTRIES'}
                      </div>
                    </button>
                    <div className="flex-1 divide-y divide-slate-800">
                      {day.entries.map((entry) => {
                        const sev = SEVERITY[entry.severity];
                        if (!sev) return null;
                        return (
                          <button
                            key={entry.id}
                            onClick={() => setSelectedDateKey(day.dateKey)}
                            className="w-full text-left p-4 hover:bg-slate-800/60 transition-colors flex gap-3"
                          >
                            <div className={`w-1.5 flex-shrink-0 self-stretch rounded-sm ${sev.bar}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-[10px] tracking-wider font-bold text-slate-300">{sev.code} · {sev.label.toUpperCase()}</span>
                                {entry.category && <span className="text-sm font-bold text-white">· {entry.category}</span>}
                                {entry.category === OSHA_CATEGORY && (
                                  <span className={`text-[8px] tracking-wider font-bold px-1.5 py-0.5 rounded ${entry.oshaNotified ? 'bg-slate-600 text-white' : 'bg-red-600 text-white'}`}>
                                    OSHA {entry.oshaNotified ? '✓' : '!'}
                                  </span>
                                )}
                              </div>
                              {entry.notes && <div className="text-sm text-slate-300 mt-1 whitespace-pre-wrap">{entry.notes}</div>}
                              <div className="text-[10px] tracking-wider text-slate-500 mt-2">
                                LOGGED BY {(entry.loggedBy || 'UNKNOWN').toUpperCase()}
                                {entry.loggedAt && ` · ${formatTime(entry.loggedAt)}`}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between text-[10px] tracking-widest text-slate-600">
          <span>ORGANIZATION-WIDE SHARED LOG</span>
          {loading && <span className="text-slate-500">SYNCING…</span>}
        </div>
      </div>

      {selectedDateKey && (
        <DayModal
          dateKey={selectedDateKey}
          dayEntries={selectedDayEntries}
          currentUserId={currentUser?.id}
          canLog={canLog}
          canManage={canManage}
          onClose={() => setSelectedDateKey(null)}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}

      {showBaseline && canManage && (
        <BaselineModal
          initial={baseline}
          onSave={handleSaveBaseline}
          onClear={handleClearBaseline}
          onClose={() => setShowBaseline(false)}
        />
      )}

      {showExport && (
        <ExportModal
          defaultMonthKey={priorMonthKey}
          onExport={handleExport}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}
